import React, { useEffect, useState } from 'react';
import { Form, InputNumber, Switch, Alert, Flex, Typography, Collapse, Select, Button, Spin } from 'antd';
import { ExperimentOutlined, PlayCircleOutlined } from '@ant-design/icons';
import GreenLightButton from '../../../components/UI/Buttons/GreenLightButton';
import LspivService from '../../../services/LspivService';

const { Title, Text } = Typography;

const DEFAULT_PARAMS = {
    interrogation_area_1: 128,
    interrogation_area_2: null,
    mask_auto: true,
    multipass: true,
    standard_filter: true,
    standard_threshold: 4,
    median_test_filter: true,
    epsilon: 0.02,
    threshold: 2,
    step: null,
    filter_grayscale: true,
    filter_clahe: true,
    clip_limit_clahe: 5,
    filter_sub_background: false,
    save_background: false,
};

const PivStep = ({ jobId, job, onRefresh }) => {
    const [form] = Form.useForm();
    const [frames, setFrames] = useState([]);
    const [testResult, setTestResult] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);

    const status = job?.status?.status;
    const step = job?.status?.step;
    const isRunning = status === 'running' && step === 'piv-analyze';
    const isReady = step === 'piv-ready';

    useEffect(() => {
        LspivService.listFrames(jobId).then(setFrames).catch(() => {});
    }, [jobId]);

    const runTest = async () => {
        const values = await form.validateFields();
        const { image_1, image_2, save_background, ...params } = values;
        setError(null); setTestResult(null); setLoading(true);
        try {
            const r = await LspivService.pivTest(jobId, {
                ...params,
                image_1,
                image_2,
            });
            setTestResult(r);
        } catch (e) {
            setError(e?.response?.data?.detail || 'Ошибка PIV-теста');
        } finally { setLoading(false); }
    };

    const runFull = async () => {
        const values = await form.validateFields();
        const { image_1, image_2, ...params } = values;
        setError(null); setLoading(true);
        try {
            await LspivService.pivAnalyze(jobId, params);
            await onRefresh();
        } catch (e) {
            setError(e?.response?.data?.detail || 'Ошибка запуска PIV');
        } finally { setLoading(false); }
    };

    const frameOptions = frames.map((f) => ({ value: f, label: f }));

    return (
        <Flex vertical gap={20}>
            <Title level={4} style={{ margin: 0 }}>
                <ExperimentOutlined /> PIV-анализ
            </Title>
            <Text type="secondary">
                FFT-based Particle Image Velocimetry. Сначала протестируйте на паре кадров,
                потом запустите полный анализ по всем кадрам (это может занять несколько минут).
            </Text>

            {error && <Alert type="error" message={error} showIcon />}
            {isRunning && (
                <Alert
                    type="info"
                    showIcon
                    message="PIV-анализ запущен в фоне"
                    description={<><Spin size="small" /> &nbsp; обновляйте страницу, пока статус не станет piv-ready</>}
                />
            )}
            {isReady && <Alert type="success" message="PIV завершён — переходите к результатам" showIcon />}

            <Form form={form} layout="vertical" initialValues={{
                ...DEFAULT_PARAMS,
                image_1: frames[0] || null,
                image_2: frames[1] || null,
            }}>
                <Flex gap={16} wrap="wrap">
                    <Form.Item name="image_1" label="Кадр 1 (для теста)" style={{ minWidth: 220 }}>
                        <Select options={frameOptions} showSearch />
                    </Form.Item>
                    <Form.Item name="image_2" label="Кадр 2 (для теста)" style={{ minWidth: 220 }}>
                        <Select options={frameOptions} showSearch />
                    </Form.Item>
                </Flex>

                <Collapse
                    items={[{
                        key: 'params',
                        label: 'Параметры PIV',
                        children: (
                            <Flex gap={12} wrap="wrap">
                                <Form.Item name="interrogation_area_1" label="interrogation_area_1"><InputNumber min={16} max={512} step={16} /></Form.Item>
                                <Form.Item name="interrogation_area_2" label="interrogation_area_2"><InputNumber min={8} max={512} step={8} /></Form.Item>
                                <Form.Item name="step" label="step (grid)"><InputNumber min={1} max={128} /></Form.Item>
                                <Form.Item name="standard_threshold" label="standard_threshold"><InputNumber min={1} max={10} /></Form.Item>
                                <Form.Item name="epsilon" label="epsilon"><InputNumber min={0.001} max={1} step={0.01} /></Form.Item>
                                <Form.Item name="threshold" label="threshold"><InputNumber min={1} max={10} /></Form.Item>
                                <Form.Item name="clip_limit_clahe" label="clip_limit_clahe"><InputNumber min={1} max={40} /></Form.Item>
                                <Form.Item name="mask_auto" label="mask_auto" valuePropName="checked"><Switch /></Form.Item>
                                <Form.Item name="multipass" label="multipass" valuePropName="checked"><Switch /></Form.Item>
                                <Form.Item name="standard_filter" label="standard_filter" valuePropName="checked"><Switch /></Form.Item>
                                <Form.Item name="median_test_filter" label="median_test_filter" valuePropName="checked"><Switch /></Form.Item>
                                <Form.Item name="filter_grayscale" label="filter_grayscale" valuePropName="checked"><Switch /></Form.Item>
                                <Form.Item name="filter_clahe" label="filter_clahe" valuePropName="checked"><Switch /></Form.Item>
                                <Form.Item name="filter_sub_background" label="filter_sub_background" valuePropName="checked"><Switch /></Form.Item>
                                <Form.Item name="save_background" label="save_background" valuePropName="checked"><Switch /></Form.Item>
                            </Flex>
                        ),
                    }]}
                />

                <Flex gap={12} style={{ marginTop: 16 }}>
                    <Button icon={<ExperimentOutlined />} onClick={runTest} loading={loading}>
                        Тест на паре
                    </Button>
                    <GreenLightButton onClick={runFull} loading={loading} disabled={isRunning}>
                        <PlayCircleOutlined /> Запустить полный PIV
                    </GreenLightButton>
                </Flex>
            </Form>

            {testResult && (
                <Alert
                    type="info"
                    message="Результат тестовой пары"
                    description={<pre style={{ margin: 0, fontSize: '0.75rem', maxHeight: 240, overflow: 'auto' }}>
                        {JSON.stringify(testResult, null, 2).slice(0, 2000)}
                    </pre>}
                />
            )}
        </Flex>
    );
};

export default PivStep;
